import React, { useState } from 'react'
import {
  Box,
  Divider,
  FormControlLabel,
  Grid,
  makeStyles,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  Typography
} from '@material-ui/core'
import { Formik, Form } from 'formik'
import { useDispatch } from 'react-redux'
import { toast } from 'utils/notify'
import * as yup from 'yup'

import { updateConferenceById } from 'store/conference'
import CustomSelect from 'components/Form/CustomSelect'
import ProgressButton from 'components/ProgressButton'
import { Collection, Roles } from 'constants/index'
import ConfirmationModal from 'components/ConfirmationModal'
import { FirestoreDatabase } from '../../../../index'

const validationSchema = yup.object().shape({
  speakerCanUploadTalks: yup.boolean(),
  enableAttendeeToChangeTimezone: yup.boolean(),
  havePolls: yup.boolean(),
  enableBadgeModule: yup.boolean(),
  badgeQrCodeFormat: yup.string().oneOf(['plain', 'vcard']),
  enableAcademics: yup.boolean(),
  enableGroupChat: yup.boolean(),
  isActive: yup.boolean(),
  hasPreviousConference: yup.string(),
  previousConferenceId: yup.string().when('hasPreviousConference', {
    is: 'yes',
    then: yup.string().required('Previous conference is required'),
    otherwise: yup.string()
  })
})

const FeatureTogglesForm = ({ conference, conferences, user }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [pendingToggle, setPendingToggle] = useState(null)

  const canManageBadgeSettings =
    user?.role === Roles.SUPER_ADMIN ||
    (user?.role === Roles.ORGANIZER && conference?.organizerId === user?.id)

  const previousConfs = () => {
    if (conference?.id) {
      return conferences.filter((conf) => conf.id !== conference.id)
    }
    return conferences
  }

  const initialValues = {
    speakerCanUploadTalks: conference?.speakerCanUploadTalks || false,
    enableAttendeeToChangeTimezone: conference?.enableAttendeeToChangeTimezone || false,
    havePolls: conference?.havePolls ?? false,
    enableBadgeModule: conference?.enableBadgeModule ?? false,
    badgeQrCodeFormat: conference?.badgeQrCodeFormat || 'plain',
    enableAcademics: conference?.enableAcademics ?? false,
    enableGroupChat: conference?.enableGroupChat ?? false,
    enableAttendeeToCreatePost: conference?.enableAttendeeToCreatePost ?? false,
    isActive: conference?.isActive ?? true,
    hasPreviousConference: conference?.previousConferenceId ? 'yes' : 'no',
    previousConferenceId: conference?.previousConferenceId ?? ''
  }

  const handleSubmit = async (values) => {
    if (!conference?.id) {
      toast.error('Conference ID is missing. Reload and try again.')
      return
    }

    const payload = {
      speakerCanUploadTalks: values.speakerCanUploadTalks,
      enableAttendeeToChangeTimezone: values.enableAttendeeToChangeTimezone,
      havePolls: values.havePolls,
      enableBadgeModule: values.enableBadgeModule,
      badgeQrCodeFormat: values.badgeQrCodeFormat,
      enableAcademics: values.enableAcademics,
      enableGroupChat: values.enableGroupChat,
      enableAttendeeToCreatePost: values.enableAttendeeToCreatePost,
      isActive: values.isActive,
      previousConferenceId:
        values.hasPreviousConference === 'yes' ? values.previousConferenceId : ''
    }

    try {
      setLoading(true)
      // If academics is being disabled, we need to delete all talks and posters
      if (conference.enableAcademics && !values.enableAcademics) {
        // Fetch all talks from this conference
        const talksSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
          .where('conferenceId', '==', conference.id)
          .get()

        // Delete all talks and their attachments
        if (!talksSnapshot.empty) {
          const batch = FirestoreDatabase.batch()

          // Process each talk
          for (const talkDoc of talksSnapshot.docs) {
            const talkId = talkDoc.id

            // Delete talk attachments first
            const attachmentsSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
              .doc(talkId)
              .collection(Collection.ATTACHMENTS)
              .get()

            // Delete each attachment document
            attachmentsSnapshot.docs.forEach((attachmentDoc) => {
              const attachmentRef = FirestoreDatabase.collection(Collection.TALKS)
                .doc(talkId)
                .collection(Collection.ATTACHMENTS)
                .doc(attachmentDoc.id)
              batch.delete(attachmentRef)
            })

            // Delete the talk document itself
            batch.delete(FirestoreDatabase.collection(Collection.TALKS).doc(talkId))
          }

          // Commit the batch delete operation
          await batch.commit()
        }
      }

      await dispatch(updateConferenceById(conference.id, payload))
      toast.success('Feature settings updated.')
    } catch (error) {
      toast.error('Could not update feature settings.')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleChange = (formikProps, field, checked) => {
    // If trying to disable academics module, show confirmation
    if (field === 'enableAcademics' && !checked && conference?.enableAcademics) {
      setPendingToggle({ field, value: checked })
      setShowConfirmation(true)
    } else {
      formikProps.setFieldValue(field, checked)
    }
  }

  const handleConfirmAcademicsDisable = async (formikProps) => {
    if (pendingToggle) {
      formikProps.setFieldValue(pendingToggle.field, pendingToggle.value)
      // The actual deletion will happen when the form is submitted
      setShowConfirmation(false)
      setPendingToggle(null)
    }
  }

  const handleCancelToggle = () => {
    // Reset the toggle to its original state
    setShowConfirmation(false)
    setPendingToggle(null)
  }

  return (
    <Paper className={classes.section}>
      <Typography variant="h6" color="secondary" className={classes.sectionTitle}>
        Feature Toggles
      </Typography>

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize>
        {({ setFieldValue, values }) => (
          <Form>
            <Grid container spacing={2}>
              {/*Todo: Hiding this item as for now, its not needed.*/}
              {/*<Grid item xs={12} md={6}>*/}
              {/*  <Box className={classes.toggleItem}>*/}
              {/*    <Typography className={classes.toggleLabel}>*/}
              {/*      Allow speakers to upload their talks*/}
              {/*    </Typography>*/}
              {/*    <Switch*/}
              {/*      color="primary"*/}
              {/*      name="speakerCanUploadTalks"*/}
              {/*      onChange={(e) => setFieldValue('speakerCanUploadTalks', e.target.checked)}*/}
              {/*      checked={values.speakerCanUploadTalks}*/}
              {/*    />*/}
              {/*  </Box>*/}
              {/*</Grid>*/}

              <Grid item xs={12} md={6}>
                <Box className={classes.toggleItem}>
                  <Typography className={classes.toggleLabel}>
                    Allow attendees to change timezone
                  </Typography>
                  <Switch
                    color="primary"
                    name="enableAttendeeToChangeTimezone"
                    onChange={(e) =>
                      setFieldValue('enableAttendeeToChangeTimezone', e.target.checked)
                    }
                    checked={values.enableAttendeeToChangeTimezone}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.toggleItem}>
                  <Typography className={classes.toggleLabel}>Enable academics module</Typography>
                  <Switch
                    color="primary"
                    name="enableAcademics"
                    onChange={(e) =>
                      handleToggleChange({ setFieldValue }, 'enableAcademics', e.target.checked)
                    }
                    checked={values.enableAcademics}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.toggleItem}>
                  <Typography className={classes.toggleLabel}>Enable group chat module</Typography>
                  <Switch
                    color="primary"
                    name="enableGroupChat"
                    onChange={(e) => setFieldValue('enableGroupChat', e.target.checked)}
                    checked={values.enableGroupChat}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.toggleItem}>
                  <Typography className={classes.toggleLabel}>
                    Allow Attendees to create community posts
                  </Typography>
                  <Switch
                    color="primary"
                    name="enableAttendeeToCreatePost"
                    onChange={(e) => setFieldValue('enableAttendeeToCreatePost', e.target.checked)}
                    checked={values.enableAttendeeToCreatePost}
                  />
                </Box>
              </Grid>

              {canManageBadgeSettings && (
                <>
                  <Grid item xs={12} md={6}>
                    <Box className={classes.toggleItem}>
                      <Typography className={classes.toggleLabel}>Enable polling module</Typography>
                      <Switch
                        color="primary"
                        name="havePolls"
                        onChange={(e) => setFieldValue('havePolls', e.target.checked)}
                        checked={values.havePolls}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box className={classes.toggleItem}>
                      <Typography className={classes.toggleLabel}>Enable badge module</Typography>
                      <Switch
                        color="primary"
                        name="enableBadgeModule"
                        onChange={(e) => setFieldValue('enableBadgeModule', e.target.checked)}
                        checked={values.enableBadgeModule}
                      />
                    </Box>
                  </Grid>

                  {values.enableBadgeModule && (
                    <Grid item xs={12}>
                      <Box className={classes.radioGroup}>
                        <Typography className={classes.radioGroupLabel}>
                          Badge QR Code Format
                        </Typography>
                        <RadioGroup
                          row
                          value={values.badgeQrCodeFormat}
                          onChange={(e) => setFieldValue('badgeQrCodeFormat', e.target.value)}>
                          <FormControlLabel
                            control={<Radio size="small" color="primary" />}
                            label="Plain text"
                            value="plain"
                          />
                          <FormControlLabel
                            control={<Radio size="small" color="primary" />}
                            label="vCard (scannable contact card)"
                            value="vcard"
                          />
                        </RadioGroup>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <Box className={classes.toggleItem}>
                      <Typography className={classes.toggleLabel}>Is Active</Typography>
                      <Switch
                        color="primary"
                        name="isActive"
                        onChange={(e) => setFieldValue('isActive', e.target.checked)}
                        checked={values.isActive}
                      />
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>

            {canManageBadgeSettings && (
              <>
                <Divider className={classes.divider} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      color="secondary"
                      className={classes.subheading}>
                      Previous Conference Link
                    </Typography>
                    <Box className={classes.radioGroup}>
                      <Typography className={classes.radioGroupLabel}>
                        Do you want to link this conference to a previous one?
                      </Typography>
                      <RadioGroup
                        row
                        value={values.hasPreviousConference}
                        onChange={(e) => setFieldValue('hasPreviousConference', e.target.value)}>
                        <FormControlLabel
                          control={<Radio size="small" color="primary" />}
                          label="Yes"
                          value="yes"
                        />
                        <FormControlLabel
                          control={<Radio size="small" color="primary" />}
                          label="No"
                          value="no"
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>

                  {values.hasPreviousConference === 'yes' && (
                    <Grid item xs={12}>
                      <CustomSelect
                        inputLabel="Previous Conference"
                        options={previousConfs()}
                        optionAttr="confName"
                        name="previousConferenceId"
                      />
                    </Grid>
                  )}
                </Grid>
              </>
            )}

            <Box className={classes.actions}>
              <ProgressButton
                type="submit"
                label="Save Changes"
                inProgress={loading}
                disabled={loading}
                color="primary"
                variant="contained"
              />
            </Box>

            <ConfirmationModal
              open={showConfirmation}
              closeModal={handleCancelToggle}
              handleConfirm={() => handleConfirmAcademicsDisable({ setFieldValue })}
              dialogTitle="Warning: Disable Academics Module"
              dialogText="This will delete all talks/posters contained in this conference. Do you want to continue?"
            />
          </Form>
        )}
      </Formik>
    </Paper>
  )
}

const useStyles = makeStyles({
  section: {
    marginBottom: '24px',
    padding: '18px 24px',
    borderRadius: '8px',
    boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
    backgroundColor: '#ffffff'
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: '20px',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px'
  },
  subheading: {
    fontWeight: 600,
    marginBottom: '8px'
  },
  divider: {
    margin: '16px 0'
  },
  toggleItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0'
  },
  toggleLabel: {
    fontSize: '14px',
    fontWeight: 500
  },
  radioGroup: {
    marginTop: '8px',
    marginBottom: '16px'
  },
  radioGroupLabel: {
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '8px',
    display: 'block'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px'
  }
})

export default FeatureTogglesForm
